import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { IoClose, IoMailOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { OrderStatusBadge } from "../orders/order-status-badge";
import { buildCustomerDetailFromOrders } from "./customers-utils";
import { GET_CUSTOMER_ORDER_HISTORY } from "../../../../Utils/Queries";
import MaButton from "../../../../Components/UI/ma-button";

export default function CustomerDetailDrawer({ open, onClose, customer }) {
  const customerId = customer?.id ?? null;

  const { data, loading, error } = useQuery(GET_CUSTOMER_ORDER_HISTORY, {
    variables: { customerId: customerId ?? "", page: null, limit: null },
    skip: !open || !customerId,
    fetchPolicy: "cache-and-network",
  });

  const items = useMemo(
    () => data?.getCustomerOrderHistory?.items ?? [],
    [data?.getCustomerOrderHistory?.items],
  );

  const detail = useMemo(
    () => (customer ? buildCustomerDetailFromOrders(customer, items) : null),
    [customer, items],
  );

  console.log(customer, "Customer");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          maxWidth: "100%",
          borderRadius: { xs: 0, sm: 0 },
          boxShadow: "-8px 0 40px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#FFFFFF",
        }}
      >
        <header
          style={{ borderBottom: "1px solid #EAEAEA" }}
          className="flex shrink-0 items-center justify-between border-b border-[#EFEFEF] px-5 py-4"
        >
          <h2 className="my-0 text-[17px] font-semibold text-[#1A1A1A]">
            Customer Details
          </h2>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              bgcolor: "#F7F7F5",
              color: "#DC2626",
              borderRadius: "10px",
              "&:hover": { bgcolor: "#FECACA" },
            }}
          >
            <IoClose className="text-xl" />
          </IconButton>
        </header>

        <Box sx={{ flex: 1, overflowY: "auto", px: 4, py: 5 }}>
          {!detail ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <p className="my-0 text-[13px] font-medium text-[#9CA3AF]">
                Select a customer to view details.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8 flex items-start justify-between gap-3">
                <div>
                  <h3 className="my-0 text-[18px] font-semibold leading-snug text-[#111827] capitalize">
                    {detail.name}
                  </h3>
                  {detail.detailLocation && detail.detailLocation !== "—" ? (
                    <p className="my-0 mt-1 text-[13px] text-[#9CA3AF]">
                      {detail.detailLocation || "No Location"}
                    </p>
                  ) : (
                    <p className="my-0 mt-1 text-[13px] text-[#9CA3AF]">
                      {"Location not available"}
                    </p>
                  )}
                </div>
                <MaButton
                  startIcon={
                    <IoMailOutline className="text-[16px]" aria-hidden />
                  }
                  variant={"primary"}
                  sx={{
                    bgcolor: "#F0F0F0",
                    color: "#2E1834",
                    borderRadius: 999,
                    boxShadow: "none",
                    border: "1px solid #EAEAEA80",
                    fontSize: "13px",
                  }}
                >
                  Send message
                </MaButton>
              </div>

              <div className="mb-10 rounded-[16px] border border-solid border-[#EAEAEA80] px-4 py-5 sm:grid-cols-3 sm:gap-4 sm:px-6">
                <div className="flex justify-between items-center text-center sm:text-left">
                  <p className="my-0 text-[11px] uppercase tracking-wide text-[#9CA3AF]">
                    First Order
                  </p>
                  <p className="my-0 mt-2 text-[13px] font-semibold leading-snug text-[#374151]">
                    {detail.firstOrderLabel}
                  </p>
                </div>
                <div className="flex justify-between items-center text-center sm:text-left">
                  <p className="my-0 text-[11px] uppercase tracking-wide text-[#9CA3AF]">
                    Last Order
                  </p>
                  <p className="my-0 mt-2 text-[13px] font-semibold leading-snug text-[#374151]">
                    {detail.lastOrderLabel}
                  </p>
                </div>
                <div className="flex justify-between items-center text-center sm:text-left">
                  <p className="my-0 text-[11px]  uppercase tracking-wide text-[#9CA3AF]">
                    Total Spent
                  </p>
                  <p className="my-0 mt-2 text-[20px] font-bold tracking-tight text-[#111827]">
                    {detail.totalSpentHighlight}
                  </p>
                </div>
              </div>
<Divider sx={{mb:3}}/>
              <Typography className="mb-4 text-[15px] font-semibold text-[#18181B]">
                Order History
              </Typography>

              {loading && items.length === 0 ? (
                <div className="flex min-h-[160px] items-center justify-center">
                  <CircularProgress size={24} sx={{ color: "#2E1834" }} />
                </div>
              ) : error ? (
                <p className="my-0 px-2 py-6 text-center text-[13px] font-medium text-[#9CA3AF]">
                  We couldn’t load this customer’s orders right now.
                </p>
              ) : detail.orderHistory.length === 0 ? (
                <p className="my-0 px-2 py-6 text-center text-[13px] font-medium text-[#9CA3AF]">
                  This customer hasn’t placed any orders yet.
                </p>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "#F8F8F8",
                        "& th": {
                          border: "none",
                          py: 1.5,
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          color: "#9CA3AF",
                        },
                      }}
                    >
                      <TableCell>ORDER ID</TableCell>
                      <TableCell align="right">PRICE</TableCell>
                      <TableCell align="center">STATUS</TableCell>
                      <TableCell align="center">ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.orderHistory.map((row) => (
                      <TableRow
                        key={row.orderRouteId || row.orderRef}
                        sx={{ "& td": { borderColor: "#F3F4F6", py: 2 } }}
                      >
                        <TableCell>
                          <span className="font-semibold text-[#1F2937]">
                            {row.orderRef}
                          </span>
                        </TableCell>
                        <TableCell align="right" className="font-semibold">
                          {row.price}
                        </TableCell>
                        <TableCell align="center">
                          <div className="flex justify-center">
                            <OrderStatusBadge status={row.status} />
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {row.orderRouteId ? (
                            <Link
                              to={`/seller/orders/${row.orderRouteId}`}
                              onClick={onClose}
                              className="text-[13px] font-semibold text-[#0088FF] hover:underline"
                            >
                              View Order
                            </Link>
                          ) : (
                            <span className="text-[12px] font-medium text-[#9CA3AF]">
                              —
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
